import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/store/use-auth-store";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  authService,
  type RegisterDto,
  type LoginDto,
  type ChangePasswordDto,
  type UpdateProfileDto,
} from "@/services/auth";
import { UtilsService } from "@/services/utils/utils.service";
import { subscriptionService } from "@/services/subscription/subscription.service";
import type { User } from "@/types/user.types";
import type { OnboardingStatus } from "@/types/user.types";
import { companyMembershipService } from "@/services/company-membership/company-membership.service";
import axios from "axios";

const utils = new UtilsService();

export const useRegister = () => {
  return useMutation({
    mutationFn: (userData: RegisterDto) => authService.register(userData),
  });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setRoles } = useAuthStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userData: LoginDto) => authService.login(userData),
    onSuccess: async (response: { accessToken?: string; refreshToken?: string }) => {
      // Backend sets tokens in HTTP-only cookies automatically
      if (response.accessToken && response.refreshToken) {
        // Get user profile to set auth state
        try {
          const profileResponse = await authService.getProfile() as { roles: string[] } & User;
          const { roles, ...user } = profileResponse;

          // If user has the Pending role, trigger OTP and redirect to verification page.
          // Compared case-insensitively to tolerate legacy 'PENDING' rows.
          if (Array.isArray(roles) && roles.some((r) => r?.toUpperCase() === 'PENDING')) {
            try {
              await authService.generateOtp(user.email, 'email_verification');
              toast.success(t('common.otpSentSuccessfully'));
            } catch (e) {
              // If OTP generation fails, still block dashboard and show error
              const errorMessage = utils.extractErrorMessage(e, t('common.otpSendFailed'));
              toast.error(errorMessage);
            }
            // Do not set auth session; redirect to verify email page
            navigate('/auth/verify-email');
            return;
          }

          if (user.status === "ENABLED") {
            setUser(user);
            setRoles(roles);
            toast.success(t("common.loginSuccessful"));

            if (roles.includes('Admin')) {
              navigate("/");
              return;
            }

            // After login, decide where to send the user:
            // 1) If redirect param exists, honor it (e.g. onboarding from marketing site)
            // 2) Otherwise, if user has no ACTIVE subscription, send to onboarding
            // 3) Else go to dashboard
            const redirect = searchParams.get('redirect');
            if (redirect) {
              navigate(redirect);
              return;
            }

            try {
              // Check onboarding status (profile completion)
              const onboardingStatus: OnboardingStatus = await authService.getOnboardingStatus();

              // Trust the backend's verdict: if onboarding is fully complete
              // (e.g. admins, invited users, users with an active company) go
              // straight to the dashboard. Avoids re-deriving company status from
              // raw memberships, which sent admins (no membership) to onboarding.
              if (onboardingStatus.currentStep === 5 || onboardingStatus.companyComplete) {
                navigate("/");
                return;
              }

              // Check profile completion first - even invited users need to complete profile
              if (!onboardingStatus.profileComplete) {
                // Profile incomplete - redirect to onboarding
                navigate("/onboarding");
                return;
              }

              // Profile is complete - check if user has companies or was invited
              const memberships = await companyMembershipService.getUserMemberships();
              if (memberships.length > 0 || onboardingStatus.wasInvited) {
                // User has companies or was invited - go to dashboard (skip company creation)
                navigate("/");
              } else {
                // Profile complete but no companies - redirect to create company
                navigate("/create-company");
              }
            } catch (error: unknown) {
              // If checks fail, try membership check first
              try {
                const memberships = await companyMembershipService.getUserMemberships();
                if (memberships.length > 0) {
                  navigate("/");
                  return;
                }
              } catch {
                // Continue to fallback
              }

              // Fallback: redirect to onboarding for profile completion
              navigate("/onboarding");
            }
          } else {
            toast.error(t("common.accountDisabled"));
          }
        } catch (error) {
          console.error("Failed to get profile after login:", error);
          toast.error(t("common.loginFailedProfile"));
        }
      } else {
        toast.error(t("common.operationFailed"));
      }
    },
    onError: async (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(error, t("common.operationFailed"));
      toast.error(errorMessage);
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      clearAuth();
      toast.success(t("common.logoutSuccessful"));
      navigate("/auth/login");
    },
    onError: () => {
      clearAuth();
      toast.info(t("common.loggedOutLocally"));
      navigate("/auth/login");
    },
  });
};

export const useSendOtp = (showToast: boolean = true) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (email: string) => authService.sendOtp(email),
    onSuccess: (response: { success?: boolean; message?: string } | null) => {
      if (response && showToast) {
        toast.success(t("common.otpSentSuccessfully"));
      }
    },
    onError: (error: unknown) => {
      if (showToast) {
        const errorMessage = utils.extractErrorMessage(error, t("common.otpSendFailed"));
        toast.error(errorMessage);
      }
    },
  });
};

// Generate OTP for email verification (or other flows)
export const useGenerateOtp = (showToast: boolean = true) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ email, type }: { email: string; type?: string }) =>
      authService.generateOtp(email, type ?? 'email_verification'),
    onSuccess: (response: { success?: boolean; message?: string } | null) => {
      if (response && showToast) {
        toast.success(t('common.otpSentSuccessfully'));
      }
    },
    onError: (error: unknown) => {
      if (showToast) {
        const errorMessage = utils.extractErrorMessage(error, t('common.otpSendFailed'));
        toast.error(errorMessage);
      }
    },
  });
};

export const useValidateOtp = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ email, OTP }: { email: string; OTP: string }) => 
      authService.validateOtp(email, OTP, 'password_reset'),
    onSuccess: (response: { success?: boolean; message?: string } | null) => {
      if (response) {
        toast.success(t("common.otpValidatedSuccessfully"));
      }
    },
    onError: (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(error, t("common.otpValidationFailed"));
      toast.error(errorMessage);
    },
  });
};

export const useVerifyEmail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setUser, setRoles } = useAuthStore();
  return useMutation({
    mutationFn: ({ email, OTP }: { email: string; OTP: string }) =>
      authService.verifyEmail({ email, OTP }),
    onSuccess: async (response: { success?: boolean; message?: string } | null) => {
      if (response?.success === true) {
        toast.success(t("common.emailVerifiedSuccessfully"));
        
        // After email verification, get user profile to authenticate
        try {
          const profileResponse = await authService.getProfile() as { roles: string[] } & User;
          const { roles, ...user } = profileResponse;

          if (user.status === "ENABLED") {
            setUser(user);
            setRoles(roles);

            // Check onboarding status to determine redirect
            try {
              // Check if user has any active memberships
              // If user has memberships, skip onboarding and go to dashboard
              const memberships = await companyMembershipService.getUserMemberships();
              if (memberships.length > 0) {
                navigate("/");
                return;
              }

              // Check onboarding status only if user has no memberships
              const onboardingStatus: OnboardingStatus = await authService.getOnboardingStatus();
              
              // If user was invited or onboarding complete, skip onboarding
              if (onboardingStatus.wasInvited === true || onboardingStatus.currentStep > 4) {
                navigate("/");
                return;
              }
              
              // Otherwise, redirect based on onboarding status
              if (onboardingStatus.currentStep <= 4) {
                navigate("/onboarding");
              } else {
                navigate("/");
              }
            } catch (error: unknown) {
              // If checks fail, try membership check first
              try {
                const memberships = await companyMembershipService.getUserMemberships();
                if (memberships.length > 0) {
                  navigate("/");
                  return;
                }
              } catch {
                // Continue to fallback
              }

              // Fallback: try subscription check
              try {
                const subscription = await subscriptionService.getMySubscription();
                if (!subscription || subscription.status !== "ACTIVE") {
                  navigate("/onboarding");
                } else {
                  navigate("/");
                }
              } catch (subError: unknown) {
                navigate("/onboarding");
              }
            }
          } else {
            toast.error(t("common.accountDisabled"));
            navigate("/auth/login");
          }
        } catch (error) {
          console.error("Failed to get profile after email verification:", error);
          toast.error(t("common.loginFailedProfile"));
          navigate("/auth/login");
        }
      } else {
        toast.error(response?.message || t("common.emailVerificationFailed"));
      }
    },
    onError: () => {
      toast.error(t("common.emailVerificationFailed"));
    },
  });
};

export const useResetPassword = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({
      email,
      OTP,
      newPassword,
    }: {
      email: string;
      OTP: string;
      newPassword: string;
    }) => authService.resetPassword({ email, OTP, newPassword }),
    onSuccess: (response: { success?: boolean; message?: string } | null) => {
      if (response) {
        toast.success(t("common.passwordResetSuccessful"));
      }
    },
    onError: (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(error, t("common.operationFailed"));
      toast.error(errorMessage);
    },
  });
};

export const useChangePassword = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: ChangePasswordDto) => authService.changePassword(data),
    onSuccess: (response: { success?: boolean; message?: string } | null) => {
      if (response) {
        toast.success(t("common.passwordChangedSuccessfully"));
      }
    },
    onError: (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(error, t("common.passwordChangeFailed"));
      toast.error(errorMessage);
    },
  });
};

export const useUpdateProfile = () => {
  return useMutation({
    mutationFn: (data: UpdateProfileDto) => authService.updateProfile(data),
  });
};

export const useUploadAvatar = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (file: File) => authService.uploadAvatar(file),
    onSuccess: (response: { success?: boolean; message?: string } | null) => {
      if (response) {
        toast.success(t("common.avatarUploadedSuccessfully"));
      }
    },
    onError: () => {
      toast.error(t("common.avatarUploadFailed"));
    },
  });
};

export const useGetProfile = () => {
  return useQuery({
    queryKey: ["userProfile"],
    queryFn: () => authService.getProfile(),
    enabled: false,
    retry: false,
  });
};
